import { useState, useEffect } from 'react';
import api from '../services/api';
import {
  LuCalendarDays, LuPlus, LuPencil, LuTrash2, LuX, LuCheck,
} from 'react-icons/lu';

const TYPE_LABEL  = { nacional: 'Nacional', estadual: 'Estadual', municipal: 'Municipal' };
const TYPE_COLOR  = { nacional: { bg:'var(--info-soft)', color:'var(--info)' }, estadual: { bg:'var(--warning-soft)', color:'var(--warning)' }, municipal: { bg:'var(--success-soft)', color:'var(--success)' } };
const YEARS       = [2024, 2025, 2026, 2027];

const emptyForm = { date:'', name:'', type:'nacional', state:'', city:'' };

function HolidayModal({ editing, onClose, onSaved }) {
  const [form,   setForm]   = useState(editing ? {
    date:  editing.date  || '',
    name:  editing.name  || '',
    type:  editing.type  || 'nacional',
    state: editing.state || '',
    city:  editing.city  || '',
  } : emptyForm);
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.date) { setError('Data é obrigatória'); return; }
    if (!form.name.trim()) { setError('Nome é obrigatório'); return; }
    setSaving(true); setError('');
    try {
      let saved;
      if (editing) {
        const { data } = await api.put(`/holidays/${editing.id}`, form);
        saved = data;
      } else {
        const { data } = await api.post('/holidays', form);
        saved = data;
      }
      onSaved(saved, !!editing);
    } catch (e) {
      setError(e.response?.data?.error || 'Erro ao salvar');
    } finally { setSaving(false); }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.38)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:16 }}>
      <div style={{ background:'var(--surface-1)', borderRadius:12, width:'100%', maxWidth:440, boxShadow:'0 12px 40px rgba(0,0,0,0.20)', maxHeight:'90vh', overflowY:'auto' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'20px 24px 0' }}>
          <div style={{ fontWeight:700, fontSize:16, color:'var(--text-1)', display:'flex', alignItems:'center', gap:8 }}>
            <LuCalendarDays color="var(--accent)" />
            {editing ? 'Editar Feriado' : 'Novo Feriado'}
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-3)' }}>
            <LuX size={17} />
          </button>
        </div>

        <div style={{ padding:'16px 24px 24px', display:'flex', flexDirection:'column', gap:12 }}>
          {error && <div className="alert-custom danger" style={{ fontSize:13 }}>{error}</div>}

          <div>
            <label className="form-label">Data *</label>
            <input type="date" className="form-control" value={form.date}
              onChange={(e) => set('date', e.target.value)} autoFocus />
          </div>
          <div>
            <label className="form-label">Nome *</label>
            <input className="form-control" value={form.name}
              onChange={(e) => set('name', e.target.value)} placeholder="Ex: Dia do Trabalho" />
          </div>
          <div>
            <label className="form-label">Abrangência</label>
            <select className="form-control" value={form.type} onChange={(e) => set('type', e.target.value)}>
              <option value="nacional">Nacional</option>
              <option value="estadual">Estadual</option>
              <option value="municipal">Municipal</option>
            </select>
          </div>
          {(form.type === 'estadual' || form.type === 'municipal') && (
            <div>
              <label className="form-label">Estado (UF)</label>
              <input className="form-control" value={form.state} maxLength={2}
                onChange={(e) => set('state', e.target.value.toUpperCase())} placeholder="Ex: SP" />
            </div>
          )}
          {form.type === 'municipal' && (
            <div>
              <label className="form-label">Município</label>
              <input className="form-control" value={form.city}
                onChange={(e) => set('city', e.target.value)} placeholder="Ex: São Paulo" />
            </div>
          )}

          <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:4 }}>
            <button onClick={onClose} style={{ padding:'9px 20px', border:'1px solid var(--border)', borderRadius:8, background:'var(--surface-1)', cursor:'pointer', fontSize:13, fontFamily:'inherit', color:'var(--text-2)' }}>
              Cancelar
            </button>
            <button onClick={handleSave} disabled={saving} style={{ padding:'9px 22px', border:'none', borderRadius:8, cursor:'pointer', fontSize:13, fontWeight:700, fontFamily:'inherit', background:'var(--accent)', color:'var(--on-accent)', display:'flex', alignItems:'center', gap:6, opacity: saving ? 0.7 : 1 }}>
              <LuCheck /> {saving ? 'Salvando…' : 'Salvar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Feriados são um catálogo global: só o administrador global altera (o backend também exige).
const isSuperAdmin = () => {
  try { return JSON.parse(localStorage.getItem('user') || '{}').is_super_admin === true; } catch { return false; }
};

export default function HolidaysPage() {
  const canEdit = isSuperAdmin();
  const [holidays,  setHolidays]  = useState([]);
  const [year,      setYear]      = useState(new Date().getFullYear());
  const [loading,   setLoading]   = useState(false);
  const [modal,     setModal]     = useState(null);
  const [error,     setError]     = useState('');
  const [success,   setSuccess]   = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get('/holidays', { params: { year } })
      .then((r) => { if (!cancelled) setHolidays(r.data); })
      .catch(() => { if (!cancelled) setError('Erro ao carregar feriados'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [year]);

  const handleSaved = (saved, isEdit) => {
    if (isEdit) {
      setHolidays((p) => p.map((h) => (h.id === saved.id ? saved : h)).sort((a,b) => a.date.localeCompare(b.date)));
    } else {
      setHolidays((p) => [...p, saved].sort((a,b) => a.date.localeCompare(b.date)));
    }
    setModal(null);
    setSuccess(isEdit ? 'Feriado atualizado' : 'Feriado cadastrado');
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleDelete = async (h) => {
    if (!window.confirm(`Remover "${h.name}"?`)) return;
    try {
      await api.delete(`/holidays/${h.id}`);
      setHolidays((p) => p.filter((x) => x.id !== h.id));
    } catch (e) {
      setError(e.response?.data?.error || 'Erro ao remover');
    }
  };

  const fmtDate = (str) => new Date(str + 'T12:00:00').toLocaleDateString('pt-BR', { weekday:'short', day:'2-digit', month:'long' });

  return (
    <div className="page-card">
      <div className="page-card-header">
        <div>
          <div className="page-card-title">Feriados</div>
          <div className="page-card-subtitle">
            Calendário de feriados nacionais, estaduais e municipais
            {!canEdit && ' — mantido pela DHR Consultoria'}
          </div>
        </div>
        {canEdit && (
          <button className="btn-salvar" onClick={() => setModal('new')}>
            <LuPlus style={{ marginRight:6 }} /> Novo Feriado
          </button>
        )}
      </div>

      {error   && <div className="alert-custom danger"  style={{ marginBottom:12 }}>{error}</div>}
      {success && <div className="alert-custom success" style={{ marginBottom:12 }}>{success}</div>}

      {/* Filtro de ano */}
      <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap' }}>
        {YEARS.map((y) => (
          <button key={y} onClick={() => setYear(y)} style={{
            padding:'5px 16px', borderRadius:6, border:'1px solid',
            borderColor: year===y ? 'var(--accent)' : 'var(--border)',
            background:  year===y ? 'var(--accent-soft)' : 'var(--surface-1)',
            color:       year===y ? 'var(--accent)' : 'var(--text-2)',
            cursor:'pointer', fontSize:13, fontWeight: year===y ? 700 : 400, fontFamily:'inherit',
          }}>{y}</button>
        ))}
      </div>

      {loading && <div style={{ color:'var(--text-3)', fontSize:13, padding:'16px 0' }}>Carregando…</div>}

      {!loading && holidays.length === 0 && (
        <div className="empty-state">
          <LuCalendarDays size={32} color="var(--text-3)" style={{ marginBottom:8 }} />
          <p>Nenhum feriado cadastrado para {year}</p>
        </div>
      )}

      {holidays.length > 0 && (
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
            <thead>
              <tr style={{ borderBottom:'2px solid var(--border)' }}>
                {['Data', 'Nome', 'Abrangência', 'Estado', 'Município', ''].map((h) => (
                  <th key={h} style={{ padding:'9px 12px', textAlign:'left', color:'var(--text-3)', fontWeight:600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {holidays.map((h) => {
                const tc = TYPE_COLOR[h.type] || TYPE_COLOR.nacional;
                return (
                  <tr key={h.id} style={{ borderBottom:'1px solid var(--border)' }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-2)'}
                    onMouseLeave={(e) => e.currentTarget.style.background='transparent'}
                  >
                    <td style={{ padding:'10px 12px', fontWeight:700, color:'var(--text-1)', whiteSpace:'nowrap' }}>{fmtDate(h.date)}</td>
                    <td style={{ padding:'10px 12px', color:'var(--text-2)' }}>{h.name}</td>
                    <td style={{ padding:'10px 12px' }}>
                      <span style={{ padding:'2px 10px', borderRadius:20, fontSize:11, fontWeight:700, background:tc.bg, color:tc.color }}>
                        {TYPE_LABEL[h.type]}
                      </span>
                    </td>
                    <td style={{ padding:'10px 12px', color:'var(--text-2)' }}>{h.state || '—'}</td>
                    <td style={{ padding:'10px 12px', color:'var(--text-2)' }}>{h.city  || '—'}</td>
                    <td style={{ padding:'10px 12px', textAlign:'right', whiteSpace:'nowrap' }}>
                      {canEdit && (
                        <>
                          <button onClick={() => setModal(h)} title="Editar" style={iconBtn('var(--text-1)')}><LuPencil size={13} /></button>
                          <button onClick={() => handleDelete(h)} title="Remover" style={iconBtn('var(--danger)')}><LuTrash2 size={13} /></button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div style={{ fontSize:12, color:'var(--text-3)', padding:'10px 4px' }}>
            {holidays.length} feriado{holidays.length !== 1 ? 's' : ''} em {year}
          </div>
        </div>
      )}

      {modal && (
        <HolidayModal
          editing={modal === 'new' ? null : modal}
          onClose={() => setModal(null)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}

const iconBtn = (color) => ({ background:'none', border:'none', cursor:'pointer', color, padding:'4px 6px', borderRadius:5 });
